import React, { useState, useEffect, useRef, useCallback } from "react";
import Map, { Marker } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import "./MapView.css";
import rawMarkers from "../../data/facilities_markers.json";
import DataSwitcher from "../DataSwitcher/DataSwitcher";
import YearSlider from "../YearSlider/YearSlider";
import MarkerIcon from "./MarkerIcon";
import { useMarkerClusters } from "../../hooks/useMarkerClusters";
import { FacilityRepository } from "../../repositories";
import Legend from "../Legend/Legend";

// Default viewport (all of India)
const DEFAULT_VIEW = {
  longitude: 78.9629,
  latitude: 20.5937,
  zoom: 4,
};

// Marker colors
const MARKER_COLOR_DEFAULT = "#9ca3af";
const MARKER_COLOR_SELECTED = "#1f2937";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Get Supabase URL from environment, extract project ref for edge function URL
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "";
const SUPABASE_PROJECT_REF = SUPABASE_URL.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || "";

// Vector tile source ID and layer ID
const DISTRICT_TILES_SOURCE_ID = "district-temps";
const DISTRICT_FILL_LAYER_ID = "district-fill";



/**
 * Build the vector tile URL for district tiles
 * @param {number} year - The year to fetch data for
 * @param {string} period - The period (e.g., 'annual', 'jun-jul-aug')
 * @returns {string} The tile URL template
 */
const buildDistrictTilesUrl = (year, period, states = [], organisations = []) => {
  // Convert period format for API (e.g., 'jun-jul-aug' -> 'jja')
  const periodMap = {
    "annual": "annual",
    "dec-jan-feb": "djf",
    "mar-apr-may": "mam",
    "jun-jul-aug": "jja",
    "sep-oct-nov": "son",
  };
  const apiPeriod = periodMap[period] || "annual";

  let url = `https://${SUPABASE_PROJECT_REF}.functions.supabase.co/district-tiles/{z}/{x}/{y}?year=${year}&period=${apiPeriod}`;
  if (states.length > 0) {
    url += `&states=${states.map(s => encodeURIComponent(s)).join(',')}`;
  }
  if (organisations.length > 0) {
    url += `&organisations=${organisations.map(o => encodeURIComponent(o)).join(',')}`;
  }
  return url;
};




function MapView({ selectedMarker, setSelectedMarker, flyToTarget, setFlyToTarget, filteredMarkers, appliedStates = [], appliedOrganisations = [] }) {
  const displayMarkers = filteredMarkers || rawMarkers;
  const mapRef = useRef();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [viewState, setViewState] = useState(() => {
    if (flyToTarget && flyToTarget.lat && flyToTarget.lng) {
      return {
        longitude: flyToTarget.lng,
        latitude: flyToTarget.lat,
        zoom: 12,
      };
    }
    return DEFAULT_VIEW;
  });
  const isInitialMount = useRef(true);
  const prevSelectedMarker = useRef(null);

  // Reset zoom to default when sidebar is closed (selectedMarker goes from truthy → null)
  useEffect(() => {
    if (prevSelectedMarker.current && !selectedMarker) {
      const map = mapRef.current?.getMap();
      if (map) {
        map.flyTo({
          center: [DEFAULT_VIEW.longitude, DEFAULT_VIEW.latitude],
          zoom: DEFAULT_VIEW.zoom,
          duration: 1200,
          essential: true,
        });
      }
    }
    prevSelectedMarker.current = selectedMarker;
  }, [selectedMarker]);

  const [selectedPeriod, setSelectedPeriod] = useState("mar-apr-may");
  const [selectedYear, setSelectedYear] = useState(2021);

  // const [isDistrictsLoading, setIsDistrictsLoading] = useState(false);
  const loadingStartTime = useRef(0);

  // Use clustering hook
  const { clusters, supercluster } = useMarkerClusters(displayMarkers, viewState, mapRef);

  // Handle flying to a facility when flyToTarget changes
  useEffect(() => {
    const flyToFacility = async () => {
      if (!flyToTarget || !flyToTarget.id) return;

      const hasCoordinates = flyToTarget.lat && flyToTarget.lng;

      // If coordinates are already present, fly to facility immediately
      if (hasCoordinates) {
        const map = mapRef.current?.getMap();
        if (map) {
          map.flyTo({
            center: [flyToTarget.lng, flyToTarget.lat],
            zoom: 12,
            duration: 1500, // 1.5 seconds
            essential: true,
          });
        } else {
          setViewState({
            longitude: flyToTarget.lng,
            latitude: flyToTarget.lat,
            zoom: 12,
          });
        }
      }

      // Show loading state with basic marker info
      setSelectedMarker({
        id: flyToTarget.id,
        name: flyToTarget.name || 'Facility',
        loading: true,
      });

      try {
        // Call Supabase edge function to get full facility data
        const facilityData = await FacilityRepository.getFacilityDailyClimate(flyToTarget.id);

        // If coordinates were not available initially, fly now using fetched data
        if (!hasCoordinates && facilityData.lat && facilityData.lng) {
          const map = mapRef.current?.getMap();
          if (map) {
            map.flyTo({
              center: [facilityData.lng, facilityData.lat],
              zoom: 12,
              duration: 1500,
              essential: true,
            });
          } else {
            setViewState({
              longitude: facilityData.lng,
              latitude: facilityData.lat,
              zoom: 12,
            });
          }
        }

        // Update with full data from edge function
        setSelectedMarker({
          id: facilityData.id,
          name: facilityData.name,
          address: facilityData.address,
          lat: facilityData.lat,
          lng: facilityData.lng,
          date: facilityData.date,
          tmin: facilityData.climate?.tmin,
          tmax: facilityData.climate?.tmax,
          humidity: facilityData.climate?.humidity_avg / 100,
          minWorkers: facilityData.min_workers,
          maxWorkers: facilityData.max_workers,
          workers: facilityData.max_workers,
          brands: facilityData.brands,
          associatedCompanies: facilityData.brands,
          loading: false,
        });
      } catch (error) {
        console.error("Failed to fetch facility data:", error);

        // Show error state
        setSelectedMarker({
          id: flyToTarget.id,
          name: flyToTarget.name || 'Facility',
          error: "Failed to load facility data. Please try again.",
          loading: false,
        });
      }

      // Clear the flyToTarget after handling
      if (setFlyToTarget) {
        setFlyToTarget(null);
      }
    };

    flyToFacility();
  }, [flyToTarget, setSelectedMarker, setFlyToTarget]);

  // Handle map load - add vector tile source and heatmap layer
  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    setMapLoaded(true);

    // Add source data loading listeners
    map.on('dataloading', (e) => {
      if (e.sourceId === DISTRICT_TILES_SOURCE_ID) {
        if (!loadingStartTime.current) {
          loadingStartTime.current = performance.now();
        }
        // setIsDistrictsLoading(true);
      }
    });

    map.on('data', (e) => {
      if (e.sourceId === DISTRICT_TILES_SOURCE_ID && e.isSourceLoaded) {
        if (loadingStartTime.current) {
          const duration = performance.now() - loadingStartTime.current;
          console.log(`District tiles loaded in ${duration.toFixed(2)}ms`);
          loadingStartTime.current = 0;
        }
        // setIsDistrictsLoading(false);
      }
    });

    map.on('error', (e) => {
      if (e.sourceId === DISTRICT_TILES_SOURCE_ID) {
        console.error("Error loading district tiles:", e);
        if (loadingStartTime.current) {
          const duration = performance.now() - loadingStartTime.current;
          console.log(`District tiles failed after ${duration.toFixed(2)}ms`);
          loadingStartTime.current = 0;
        }
        // setIsDistrictsLoading(false);
      }
    });

    // Add District tiles source and layer
    if (!map.getSource(DISTRICT_TILES_SOURCE_ID)) {
      map.addSource(DISTRICT_TILES_SOURCE_ID, {
        type: "vector",
        tiles: [buildDistrictTilesUrl(selectedYear, selectedPeriod, appliedStates, appliedOrganisations)],
        minzoom: 0,
        maxzoom: 14,
      });

      map.addLayer({
        id: DISTRICT_FILL_LAYER_ID,
        type: "fill",
        source: DISTRICT_TILES_SOURCE_ID,
        "source-layer": "districts", // Name from ST_AsMVT second argument
        layout: {
          visibility: "visible",
        },
        paint: {
          "fill-color": [
            "interpolate",
            ["linear"],
            ["to-number", ["get", "avg_temp"]],

            20, "#2c7bb6",   // Comfortable blue
            25, "#abd9e9",   // Mild

            30, "#ffffbf",   // Warm threshold
            32, "#fee08b",   // Rising stress

            34, "#fdae61",   // High caution
            36, "#f46d43",   // Severe heat stress (important band)

            38, "#d73027",   // Very high risk
            40, "#a50026",   // Extreme

            45, "#6a000f"    // Upper
          ],
          // paint: {
          //   // Option 1: Fine-grained brackets for temperature
          //   "fill-color": [
          //     "interpolate",
          //     ["linear"],
          //     ["to-number", ["get", "avg_temp"]],
          //     10, "#313695",
          //     20, "#4575b4",
          //     25, "#74add1",
          //     30, "#fee090",
          //     35, "#fdae61",
          //     39, "#f46d43",   // 35-39
          //     42, "#d73027",   // 39-42
          //     46, "#a50026",   // 42-46
          //     50, "#67001f"    // 46-50
          //   ],
          /*
          // Option 2: Original gradient (for reference)
          "fill-color": [
            "interpolate",
            ["linear"],
            ["to-number", ["get", "avg_temp"]],
            10, "#313695",
            20, "#4575b4",
            25, "#74add1",
            30, "#fee090",
            35, "#fdae61",
            40, "#f46d43",
            50, "#a50026"
          ],
          */
          "fill-opacity": 0.7,
          "fill-outline-color": "#ffffff"
        }
      }, "waterway-label"); // Place below labels if possible, or just append
    }
  }, [selectedYear, selectedPeriod, appliedStates, appliedOrganisations]);



  // Update District tiles URL when year, period, or state filters change
  useEffect(() => {
    if (!mapLoaded) return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    const source = map.getSource(DISTRICT_TILES_SOURCE_ID);
    if (source) {
      const newUrl = buildDistrictTilesUrl(selectedYear, selectedPeriod, appliedStates, appliedOrganisations);
      source.setTiles([newUrl]);
      // Explicitly set loading to true when changing tiles, in case dataloading event is missed or throttled
      // setIsDistrictsLoading(true);
      loadingStartTime.current = performance.now();
    }
  }, [selectedYear, selectedPeriod, appliedStates, appliedOrganisations, mapLoaded]);

  // Auto-adjust viewport to fit filtered markers
  useEffect(() => {
    // Skip on initial mount — we don't want to fly on first render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!mapLoaded) return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    // Skip resetting/fitting viewport bounds if a facility is selected or being flown to
    if (selectedMarker || flyToTarget) return;

    // If showing all markers (no filter), reset to default India view
    if (!filteredMarkers || displayMarkers.length === rawMarkers.length) {
      map.flyTo({
        center: [DEFAULT_VIEW.longitude, DEFAULT_VIEW.latitude],
        zoom: DEFAULT_VIEW.zoom,
        duration: 1200,
        essential: true,
      });
      return;
    }

    // Need at least one marker to compute bounds
    if (displayMarkers.length === 0) return;

    // Compute bounding box from all visible markers
    const bounds = displayMarkers.reduce(
      (b, m) => {
        return {
          minLng: Math.min(b.minLng, m.lng),
          maxLng: Math.max(b.maxLng, m.lng),
          minLat: Math.min(b.minLat, m.lat),
          maxLat: Math.max(b.maxLat, m.lat),
        };
      },
      { minLng: Infinity, maxLng: -Infinity, minLat: Infinity, maxLat: -Infinity }
    );

    map.fitBounds(
      [
        [bounds.minLng, bounds.minLat],
        [bounds.maxLng, bounds.maxLat],
      ],
      {
        padding: 60,
        maxZoom: 12,
        duration: 1200,
      }
    );
  }, [displayMarkers, filteredMarkers, mapLoaded, selectedMarker, flyToTarget]);


  return (
    <div className="map-container">
      {/* {isDistrictsLoading && (
        <div className="map-loading-indicator">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <span>Fetching temperature data...</span>
          </div>
        </div>
      )} */}
      <div className="map-area">
        <Map
          ref={mapRef}
          {...viewState}
          onMove={(evt) => setViewState(evt.viewState)}
          onLoad={handleMapLoad}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/mapbox/light-v10"
          mapboxAccessToken={MAPBOX_TOKEN}
        >

          {/* ✅ Clustered Markers (Rendered in all modes) */}
          {clusters.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const { cluster: isCluster, point_count: pointCount } = cluster.properties;

            if (isCluster) {
              // Render cluster
              return (
                <Marker
                  key={`cluster-${cluster.id}`}
                  longitude={longitude}
                  latitude={latitude}
                  anchor="center"
                >
                  <div
                    className="cluster-marker"
                    style={{
                      width: `${30 + (pointCount / displayMarkers.length) * 40}px`,
                      height: `${30 + (pointCount / displayMarkers.length) * 40}px`,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      // Zoom into cluster with smooth animation
                      const expansionZoom = Math.min(
                        supercluster.getClusterExpansionZoom(cluster.id),
                        20
                      );
                      const map = mapRef.current?.getMap();
                      if (map) {
                        map.flyTo({
                          center: [longitude, latitude],
                          zoom: expansionZoom,
                          duration: 800, // 0.8 seconds for cluster zoom
                          essential: true,
                        });
                      } else {
                        setViewState({
                          ...viewState,
                          longitude,
                          latitude,
                          zoom: expansionZoom,
                        });
                      }
                    }}
                  >
                    <span className="cluster-count">{pointCount}</span>
                  </div>
                </Marker>
              );
            }

            // Render individual marker
            const marker = displayMarkers.find((m) => m.id === cluster.properties.markerId);
            if (!marker) return null;

            const isSelected = selectedMarker?.id === marker.id;

            return (
              <Marker
                key={`marker-${marker.id}`}
                longitude={longitude}
                latitude={latitude}
                anchor="bottom"
              >
                <div
                  className={`marker-container ${isSelected ? "marker-selected" : ""}`}
                  style={{ textAlign: "center", cursor: "pointer" }}
                  onClick={async () => {
                    console.log("Marker clicked:", marker);

                    // Smooth fly to the marker location
                    const map = mapRef.current?.getMap();
                    if (map) {
                      map.flyTo({
                        center: [longitude, latitude],
                        zoom: Math.max(map.getZoom(), 12), // Zoom in if not already zoomed
                        duration: 1000, // 1 second
                        essential: true,
                      });
                    }

                    // Show loading state with basic marker info
                    setSelectedMarker({
                      id: marker.id,
                      name: marker.name,
                      loading: true,
                    });

                    try {
                      // Call Supabase edge function to get full facility data
                      const facilityData = await FacilityRepository.getFacilityDailyClimate(marker.id);

                      // Update with full data from edge function
                      setSelectedMarker({
                        id: facilityData.id,
                        name: facilityData.name,
                        address: facilityData.address,
                        lat: facilityData.lat,
                        lng: facilityData.lng,
                        date: facilityData.date,
                        tmin: facilityData.climate?.tmin,
                        tmax: facilityData.climate?.tmax,
                        humidity: facilityData.climate?.humidity_avg / 100, // Convert to decimal
                        minWorkers: facilityData.min_workers,
                        maxWorkers: facilityData.max_workers,
                        workers: facilityData.max_workers, // Use max_workers as display value
                        brands: facilityData.brands,
                        associatedCompanies: facilityData.brands, // Map brands to associatedCompanies for sidebar
                        loading: false,
                      });
                    } catch (error) {
                      console.error("Failed to fetch facility data:", error);

                      // Show error state
                      setSelectedMarker({
                        id: marker.id,
                        name: marker.name,
                        error: "Failed to load facility data. Please try again.",
                        loading: false,
                      });
                    }
                  }}
                >
                  <MarkerIcon
                    size={isSelected ? 42 : 30}
                    color={isSelected ? MARKER_COLOR_SELECTED : MARKER_COLOR_DEFAULT}
                  />
                </div>
              </Marker>
            );
          })}
        </Map>



        {/* Time Controls - Data Switcher Component */}
        <DataSwitcher
          selectedPeriod={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
        />

        {/* Time Controls - Year Slider Component */}
        <YearSlider
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
        />

        {/* Invisible bounding box to act as a unified target for the onboarding highlight */}
        <div
          className="onboarding-time-controls-target"
          style={{
            position: 'absolute',
            bottom: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: '1000px',
            height: '140px',
            pointerEvents: 'none',
            zIndex: 1000
          }}
        />
      </div>
      {/* Temperature Legend */}
      <Legend />
    </div>
  );
}

export default MapView;
